import React, { useState } from 'react';
import { Award, Trophy, Star, MessageSquare, Users, HelpCircle, ChevronDown, GraduationCap, Quote } from 'lucide-react';
import type { BlockRenderProps } from './types';
import { EditableText } from './EditableText';

export const TopperCardRenderer: React.FC<BlockRenderProps> = ({ node, isEditor, onUpdateProps }) => {
  const studentName = (node.props?.studentName as string) || 'Aarav Sharma';
  const examName = (node.props?.examName as string) || 'JEE Advanced 2025';
  const rank = (node.props?.rank as string) || 'AIR 42';
  const score = (node.props?.score as string) || 'Score: 312/360';
  const photoUrl = (node.props?.photoUrl as string) || 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=400&auto=format&fit=crop&q=80';
  const testimonial = (node.props?.testimonial as string) || 'The structured test series and faculty mentorship were pivotal in my preparation.';
  const batchYear = (node.props?.batchYear as string) || 'Class of 2025';

  return (
    <div className="rounded-2xl border border-border bg-card p-5 text-center shadow-sm relative overflow-hidden flex flex-col items-center">
      <div className="absolute top-3 right-3 text-amber-500">
        <Star className="h-4 w-4 fill-amber-500" />
      </div>

      <div className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-extrabold bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-200 mb-3 shadow-sm">
        <Award className="h-3.5 w-3.5" />
        <EditableText
          as="span"
          value={rank}
          isEditor={isEditor}
          onChange={(newVal) => onUpdateProps?.(node.id, { rank: newVal })}
        />
      </div>

      <div className="w-20 h-20 rounded-full overflow-hidden border-2 border-primary shadow mb-3">
        <img
          src={photoUrl}
          alt={studentName}
          className="w-full h-full object-cover"
          loading="lazy"
        />
      </div>

      <EditableText
        as="h3"
        value={studentName}
        isEditor={isEditor}
        onChange={(newVal) => onUpdateProps?.(node.id, { studentName: newVal })}
        className="font-bold text-base text-foreground leading-tight block"
      />
      <EditableText
        as="div"
        value={examName}
        isEditor={isEditor}
        onChange={(newVal) => onUpdateProps?.(node.id, { examName: newVal })}
        className="text-xs font-semibold text-primary mt-0.5 block"
      />
      {score && (
        <EditableText
          as="div"
          value={score}
          isEditor={isEditor}
          onChange={(newVal) => onUpdateProps?.(node.id, { score: newVal })}
          className="text-xs text-muted-foreground mt-0.5 block"
        />
      )}

      {testimonial && (
        <blockquote className="text-xs text-muted-foreground italic mt-3 pt-3 border-t border-border w-full leading-relaxed">
          &ldquo;
          <EditableText
            as="span"
            value={testimonial}
            isEditor={isEditor}
            onChange={(newVal) => onUpdateProps?.(node.id, { testimonial: newVal })}
            multiline
          />
          &rdquo;
        </blockquote>
      )}

      {batchYear && (
        <EditableText
          as="div"
          value={batchYear}
          isEditor={isEditor}
          onChange={(newVal) => onUpdateProps?.(node.id, { batchYear: newVal })}
          className="text-[10px] text-muted-foreground/75 mt-2 uppercase tracking-wider font-semibold block"
        />
      )}
    </div>
  );
};

export const ToppersShowcaseRenderer: React.FC<BlockRenderProps> = ({ node, children, isEditor, onUpdateProps }) => {
  const headline = (node.props?.headline as string) || (node.props?.title as string) || 'Our Star Performers & Toppers';
  const subheadline = (node.props?.subheadline as string) || 'Celebrating outstanding ranks and national achievement records.';
  const badgeText = (node.props?.badgeText as string) || 'Hall of Fame 2025';

  return (
    <div className="py-8 w-full text-center">
      <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-bold uppercase tracking-wider mb-2">
        <Trophy className="h-3.5 w-3.5" />
        <EditableText
          as="span"
          value={badgeText}
          isEditor={isEditor}
          onChange={(newVal) => onUpdateProps?.(node.id, { badgeText: newVal })}
        />
      </div>
      <EditableText
        as="h2"
        value={headline}
        isEditor={isEditor}
        onChange={(newVal) => onUpdateProps?.(node.id, { headline: newVal, title: newVal })}
        className="text-2xl md:text-3xl font-extrabold text-foreground tracking-tight mb-2 block"
      />
      <EditableText
        as="p"
        value={subheadline}
        isEditor={isEditor}
        onChange={(newVal) => onUpdateProps?.(node.id, { subheadline: newVal })}
        className="text-sm text-muted-foreground max-w-xl mx-auto mb-6 block"
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 text-left">
        {children || (
          <div className="col-span-full p-8 border border-dashed border-border rounded-xl text-center text-sm text-muted-foreground">
            Drop Topper Achievement Cards inside this showcase section.
          </div>
        )}
      </div>
    </div>
  );
};

export const TestimonialsRenderer: React.FC<BlockRenderProps> = ({ node, isEditor, onUpdateProps }) => {
  const headline = (node.props?.headline as string) || 'Success Stories from Our Rankers';
  const subheadline = (node.props?.subheadline as string) || 'Hear how our structured pedagogy and mentoring helped students crack top national entrance exams.';

  const t1 = {
    name: (node.props?.testimonial1_name as string) || 'Rohan Deshmukh',
    role: (node.props?.testimonial1_role as string) || 'AIR 42 • JEE Advanced 2025',
    quote: (node.props?.testimonial1_quote as string) || 'The daily DPPs and rigorous doubt sessions at Helios Academy helped me master organic chemistry and physics concepts with crystal clarity.',
    badge: (node.props?.testimonial1_badge as string) || 'IIT Bombay CSE',
  };
  const t2 = {
    name: (node.props?.testimonial2_name as string) || 'Ananya Sharma',
    role: (node.props?.testimonial2_role as string) || 'Score: 710/720 • NEET UG 2025',
    quote: (node.props?.testimonial2_quote as string) || 'The faculty treated my weaknesses as their own. Personal feedback on mock tests made all the difference on the final exam day.',
    badge: (node.props?.testimonial2_badge as string) || 'AIIMS New Delhi',
  };
  const t3 = {
    name: (node.props?.testimonial3_name as string) || 'Dr. Sunil Varma (Parent)',
    role: (node.props?.testimonial3_role as string) || 'Father of Kshitij Varma (AIR 104)',
    quote: (node.props?.testimonial3_quote as string) || 'As a parent, the constant attendance alerts, progress reports, and mentor guidance gave us total peace of mind throughout the 2-year preparation.',
    badge: (node.props?.testimonial3_badge as string) || 'Parent Review',
  };

  const list = [
    { ...t1, prefix: 'testimonial1' },
    { ...t2, prefix: 'testimonial2' },
    { ...t3, prefix: 'testimonial3' },
  ];

  return (
    <div className="py-8 w-full text-center">
      <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-bold uppercase tracking-wider mb-2">
        <MessageSquare className="h-3.5 w-3.5" />
        <span>Verified Reviews</span>
      </div>
      <EditableText
        as="h2"
        value={headline}
        isEditor={isEditor}
        onChange={(newVal) => onUpdateProps?.(node.id, { headline: newVal })}
        className="text-2xl md:text-3xl font-extrabold text-foreground tracking-tight mb-2 block"
      />
      <EditableText
        as="p"
        value={subheadline}
        isEditor={isEditor}
        onChange={(newVal) => onUpdateProps?.(node.id, { subheadline: newVal })}
        className="text-xs md:text-sm text-muted-foreground max-w-xl mx-auto mb-8 block"
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 text-left">
        {list.map((item, idx) => (
          <div key={idx} className="p-5 rounded-2xl border border-border bg-card shadow-xs flex flex-col justify-between hover:shadow-sm transition-all">
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="flex text-amber-500 gap-0.5">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-3.5 w-3.5 fill-amber-500" />
                  ))}
                </div>
                {item.badge && (
                  <EditableText
                    as="span"
                    value={item.badge}
                    isEditor={isEditor}
                    onChange={(val) => onUpdateProps?.(node.id, { [`${item.prefix}_badge`]: val })}
                    className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-primary/10 text-primary block"
                  />
                )}
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed italic mb-4">
                &ldquo;
                <EditableText
                  as="span"
                  value={item.quote}
                  isEditor={isEditor}
                  onChange={(val) => onUpdateProps?.(node.id, { [`${item.prefix}_quote`]: val })}
                  multiline
                />
                &rdquo;
              </p>
            </div>
            <div className="pt-3 border-t border-border flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-full bg-primary/10 text-primary font-bold text-xs flex items-center justify-center shrink-0">
                {item.name.charAt(0)}
              </div>
              <div>
                <EditableText
                  as="div"
                  value={item.name}
                  isEditor={isEditor}
                  onChange={(val) => onUpdateProps?.(node.id, { [`${item.prefix}_name`]: val })}
                  className="text-xs font-bold text-foreground block"
                />
                <EditableText
                  as="div"
                  value={item.role}
                  isEditor={isEditor}
                  onChange={(val) => onUpdateProps?.(node.id, { [`${item.prefix}_role`]: val })}
                  className="text-[10px] text-muted-foreground block"
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const FacultyShowcaseRenderer: React.FC<BlockRenderProps> = ({ node, isEditor, onUpdateProps }) => {
  const headline = (node.props?.headline as string) || 'Meet Our Master Educators';
  const subheadline = (node.props?.subheadline as string) || 'Learn from distinguished educators, ex-IITians, and subject masters dedicated to student rank achievement.';

  const f1 = {
    name: (node.props?.faculty1_name as string) || 'Prof. Arvind Trivedi',
    subject: (node.props?.faculty1_subject as string) || 'Advanced Physics',
    qualification: (node.props?.faculty1_qualification as string) || 'B.Tech IIT Kanpur (AIR 38)',
    exp: (node.props?.faculty1_exp as string) || '18+ Years Exp',
    bio: (node.props?.faculty1_bio as string) || 'Mentor to 120+ Top 100 AIR rankers. Renowned for intuitive mechanics and electrodynamics problem solving.',
  };
  const f2 = {
    name: (node.props?.faculty2_name as string) || 'Dr. Meenakshi Sunder',
    subject: (node.props?.faculty2_subject as string) || 'Organic & Inorganic Chemistry',
    qualification: (node.props?.faculty2_qualification as string) || 'Ph.D. IISc Bangalore',
    exp: (node.props?.faculty2_exp as string) || '14+ Years Exp',
    bio: (node.props?.faculty2_bio as string) || 'Specializes in reaction mechanism mastery and mnemonics that make complex transformations effortless.',
  };
  const f3 = {
    name: (node.props?.faculty3_name as string) || 'Er. Rajesh Nair',
    subject: (node.props?.faculty3_subject as string) || 'Pure & Applied Mathematics',
    qualification: (node.props?.faculty3_qualification as string) || 'M.Tech IIT Kharagpur',
    exp: (node.props?.faculty3_exp as string) || '12+ Years Exp',
    bio: (node.props?.faculty3_bio as string) || 'Author of bestselling entrance problem sets in calculus, coordinate geometry, and combinatorial analysis.',
  };

  const list = [
    { ...f1, prefix: 'faculty1' },
    { ...f2, prefix: 'faculty2' },
    { ...f3, prefix: 'faculty3' },
  ];

  return (
    <div className="py-8 w-full text-center">
      <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-bold uppercase tracking-wider mb-2">
        <Users className="h-3.5 w-3.5" />
        <span>Faculty Pedagogy</span>
      </div>
      <EditableText
        as="h2"
        value={headline}
        isEditor={isEditor}
        onChange={(newVal) => onUpdateProps?.(node.id, { headline: newVal })}
        className="text-2xl md:text-3xl font-extrabold text-foreground tracking-tight mb-2 block"
      />
      <EditableText
        as="p"
        value={subheadline}
        isEditor={isEditor}
        onChange={(newVal) => onUpdateProps?.(node.id, { subheadline: newVal })}
        className="text-xs md:text-sm text-muted-foreground max-w-xl mx-auto mb-8 block"
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 text-left">
        {list.map((fac, idx) => (
          <div key={idx} className="p-5 rounded-2xl border border-border bg-card shadow-xs flex flex-col justify-between hover:border-primary/40 transition-all">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center font-bold text-base shrink-0">
                  <GraduationCap className="h-6 w-6" />
                </div>
                <div>
                  <EditableText
                    as="h3"
                    value={fac.name}
                    isEditor={isEditor}
                    onChange={(val) => onUpdateProps?.(node.id, { [`${fac.prefix}_name`]: val })}
                    className="font-bold text-sm text-foreground block"
                  />
                  <EditableText
                    as="div"
                    value={fac.subject}
                    isEditor={isEditor}
                    onChange={(val) => onUpdateProps?.(node.id, { [`${fac.prefix}_subject`]: val })}
                    className="text-xs font-semibold text-primary block"
                  />
                </div>
              </div>

              <div className="inline-block px-2 py-0.5 rounded bg-muted text-[10px] font-semibold text-muted-foreground mb-3">
                <EditableText
                  as="span"
                  value={fac.qualification}
                  isEditor={isEditor}
                  onChange={(val) => onUpdateProps?.(node.id, { [`${fac.prefix}_qualification`]: val })}
                />
                {' • '}
                <EditableText
                  as="span"
                  value={fac.exp}
                  isEditor={isEditor}
                  onChange={(val) => onUpdateProps?.(node.id, { [`${fac.prefix}_exp`]: val })}
                />
              </div>

              <p className="text-xs text-muted-foreground leading-relaxed">
                <EditableText
                  as="span"
                  value={fac.bio}
                  isEditor={isEditor}
                  onChange={(val) => onUpdateProps?.(node.id, { [`${fac.prefix}_bio`]: val })}
                  multiline
                />
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const AccordionFAQRenderer: React.FC<BlockRenderProps> = ({ node, isEditor, onUpdateProps }) => {
  const headline = (node.props?.headline as string) || 'Frequently Asked Questions';
  const subheadline = (node.props?.subheadline as string) || 'Everything you need to know about admissions, curriculum, and results.';

  const q1 = (node.props?.q1 as string) || 'What is the admission and enrollment process?';
  const a1 = (node.props?.a1 as string) || 'Admissions are granted based on the Entrance Diagnostic Test (EDT) or direct registration for foundation batches. Speak with our counselors to get started.';
  const q2 = (node.props?.q2 as string) || 'Are study materials and test series included?';
  const a2 = (node.props?.a2 as string) || 'Yes, all enrolled classroom and online students receive comprehensive module booklets, daily practice problems (DPPs), and national-level test series access.';
  const q3 = (node.props?.q3 as string) || 'What are the typical batch timings and student-to-teacher ratio?';
  const a3 = (node.props?.a3 as string) || 'We maintain an optimal ratio of 25:1 to ensure personalized attention and dedicated doubt-clearing sessions.';
  const q4 = (node.props?.q4 as string) || 'Can I attend demo lectures before paying tuition fees?';
  const a4 = (node.props?.a4 as string) || 'Yes! We offer 2 complimentary trial lectures so students can experience our teaching methodology and faculty expertise first-hand.';

  const faqs = [
    { q: q1, a: a1, keyQ: 'q1', keyA: 'a1' },
    { q: q2, a: a2, keyQ: 'q2', keyA: 'a2' },
    { q: q3, a: a3, keyQ: 'q3', keyA: 'a3' },
    { q: q4, a: a4, keyQ: 'q4', keyA: 'a4' },
  ];

  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <div className="py-8 w-full max-w-3xl mx-auto">
      <div className="text-center mb-6">
        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-bold uppercase tracking-wider mb-2">
          <HelpCircle className="h-3.5 w-3.5" />
          <span>Support & Admissions</span>
        </div>
        <EditableText
          as="h2"
          value={headline}
          isEditor={isEditor}
          onChange={(newVal) => onUpdateProps?.(node.id, { headline: newVal })}
          className="text-2xl md:text-3xl font-extrabold text-foreground tracking-tight mb-2 block"
        />
        {subheadline && (
          <EditableText
            as="p"
            value={subheadline}
            isEditor={isEditor}
            onChange={(newVal) => onUpdateProps?.(node.id, { subheadline: newVal })}
            className="text-xs md:text-sm text-muted-foreground block"
          />
        )}
      </div>

      <div className="space-y-3">
        {faqs.map((faq, index) => {
          const isOpen = openIndex === index;
          return (
            <div
              key={index}
              className="rounded-xl border border-border bg-card overflow-hidden shadow-xs transition-all"
            >
              <div
                onClick={() => setOpenIndex(isOpen ? null : index)}
                className="w-full p-4 text-left flex items-center justify-between gap-3 text-xs md:text-sm font-bold text-foreground hover:bg-muted/40 transition-colors cursor-pointer"
              >
                <EditableText
                  as="span"
                  value={faq.q}
                  isEditor={isEditor}
                  onChange={(val) => onUpdateProps?.(node.id, { [faq.keyQ]: val })}
                />
                <ChevronDown
                  className={`h-4 w-4 text-muted-foreground transition-transform duration-200 shrink-0 ${
                    isOpen ? 'rotate-180 text-primary' : ''
                  }`}
                />
              </div>
              {isOpen && (
                <div className="px-4 pb-4 pt-1 text-xs text-muted-foreground leading-relaxed border-t border-border/40 bg-muted/10">
                  <EditableText
                    as="p"
                    value={faq.a}
                    isEditor={isEditor}
                    onChange={(val) => onUpdateProps?.(node.id, { [faq.keyA]: val })}
                    multiline
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
